import { useEffect, useState } from "react";
import { BellRinging, BellSlash, ShieldWarning } from "@phosphor-icons/react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { http, apiError } from "@/lib/api";

/**
 * NotificationSettings — persistent, always-visible toggle for browser push
 * notifications. Unlike NotificationPermissionPrompt (which is a one-time
 * auto-nag on login), this lives on the Stats page so a client can turn push
 * ON or OFF at any time — critical for clients who dismissed the auto-prompt
 * or want to re-subscribe after re-installing the PWA.
 *
 * States:
 *   - Unsupported: hidden explanation card ("your browser doesn't support")
 *   - Denied at OS level: yellow instructions to enable in iOS Settings
 *   - Not subscribed but promptable: red "Enable" CTA
 *   - Subscribed: green "You're locked in" + Disable button
 */
export default function NotificationSettings() {
  const { supported, permission, subscribed, busy, subscribe, unsubscribe } = usePushNotifications();
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const { data } = await http.get("/push/status");
        setStatus(data);
      } catch { /* silent — diagnostic is best-effort */ }
    })();
  }, [supported, subscribed]);

  const sendTest = async () => {
    setTesting(true); setTestMsg("");
    try {
      await http.post("/push/test");
      setTestMsg("✓ Test push fired. Check your notifications tray (may take 5-10s).");
      setTimeout(() => setTestMsg(""), 6000);
    } catch (e) {
      setTestMsg(apiError(e, "Test push failed."));
    } finally { setTesting(false); }
  };

  const resetAllSubs = async () => {
    // Escape hatch — wipes every push sub tied to this user (across all devices)
    // so they can start fresh. Used when 'Send test push' keeps failing because
    // subs registered against an old VAPID key are stuck in the DB.
    if (!window.confirm(
      "Clear ALL registered devices?\n\n" +
      "This wipes every browser & phone you've enabled push on. Tap 'Turn on notifications' after to re-register this device.\n\n" +
      "Use this if test pushes keep failing."
    )) return;
    setMsg(""); setTestMsg("");
    try {
      const { data } = await http.delete("/push/subscriptions/mine");
      setStatus({ user_id: status?.user_id, active_subscriptions: 0, latest_registered_at: null, devices: [] });
      setMsg(`Cleared ${data.removed} subscription${data.removed === 1 ? "" : "s"}. Tap 'Turn on notifications' to re-enable this device.`);
      if (subscribed) {
        try { await unsubscribe(); } catch { /* best-effort */ }
      }
    } catch (e) {
      setMsg(apiError(e, "Couldn't clear subscriptions."));
    }
  };

  const enable = async () => {
    setMsg("");
    const r = await subscribe();
    if (r?.ok) {
      setMsg("Notifications ON — you'll get pinged when Coach sends a message.");
    } else if (r?.reason === "denied") {
      setMsg("Blocked at the OS level. See the fix instructions below.");
    } else if (r?.reason === "server-not-configured") {
      setMsg("Server-side push isn't ready. Ping your coach.");
    } else if (r?.reason === "error" && r?.error) {
      // Show the actual error string so the user can screenshot it back to
      // the coach if debugging is needed. "Service worker not ready (8s
      // timeout)" is the most common cause — indicates iOS Safari (not
      // installed to Home Screen) or a corrupt SW cache.
      setMsg(`Couldn't enable: ${r.error}. On iPhone: install to Home Screen and open from that icon. If already installed: fully close the app and reopen.`);
    } else {
      setMsg("Couldn't enable — on iPhone, make sure the app is installed to your Home Screen first, then try again.");
    }
  };

  const disable = async () => {
    if (!window.confirm("Turn notifications OFF? You won't be pinged when Coach messages you.")) return;
    setMsg("");
    await unsubscribe();
    setMsg("Notifications OFF.");
  };

  if (!supported) {
    return (
      <div className="id-card mb-6" data-testid="notifications-unsupported">
        <div className="flex items-center gap-3">
          <BellSlash size={18} weight="bold" className="text-zinc-500" />
          <div>
            <div className="id-label text-zinc-500">Notifications</div>
            <p className="text-zinc-400 text-xs font-manrope mt-1">
              Your browser doesn&apos;t support push. On iPhone, make sure you&apos;ve added the app to your Home Screen (Share → Add to Home Screen), then open the app from that icon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const denied = permission === "denied";
  const on = subscribed && permission === "granted";

  return (
    <div
      className={
        "id-card mb-6 " +
        (on ? "border-[#FF3333]/40 bg-[#FF3333]/5" : denied ? "border-yellow-500/50 bg-yellow-500/5" : "border-[#262626]")
      }
      data-testid="notifications-settings"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {on ? (
            <BellRinging size={22} weight="fill" className="text-[#FF3333]" />
          ) : denied ? (
            <ShieldWarning size={22} weight="fill" className="text-yellow-500" />
          ) : (
            <BellSlash size={22} weight="bold" className="text-zinc-500" />
          )}
          <div>
            <div className={"id-label " + (on ? "text-[#FF3333]" : denied ? "text-yellow-500" : "text-zinc-400")}>
              Notifications · {on ? "ON" : denied ? "BLOCKED" : "OFF"}
            </div>
            <p className="text-zinc-300 text-xs font-manrope mt-1 max-w-md">
              {on
                ? "You'll get pinged instantly when Coach messages you, sends a nudge, or drops a new meal/plan."
                : denied
                ? "You blocked notifications for this app. Fix it below."
                : "Get pinged when Coach messages you or drops a new plan. No spam, no marketing — just training touch-points."}
            </p>
          </div>
        </div>
        {!denied && (
          on ? (
            <button
              onClick={disable}
              disabled={busy}
              className="id-btn-ghost text-xs border-[#333] text-zinc-300 hover:border-white hover:text-white disabled:opacity-40 shrink-0"
              data-testid="notifications-disable-btn"
            >
              {busy ? "..." : "Turn off"}
            </button>
          ) : (
            <button
              onClick={enable}
              disabled={busy}
              className="id-btn-primary text-xs disabled:opacity-40 shrink-0"
              data-testid="notifications-enable-btn"
            >
              <BellRinging size={14} weight="fill" />
              {busy ? "Enabling..." : "Turn on notifications"}
            </button>
          )
        )}
      </div>

      {msg && (
        <div className="mt-3 text-xs font-manrope text-zinc-300 border-l-2 border-[#FF3333] pl-3" data-testid="notifications-message">
          {msg}
        </div>
      )}

      {/* Diagnostic — active device count + test-push button so the client
          can self-verify pushes work end-to-end. */}
      {status && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#262626] pt-3" data-testid="notifications-diagnostic">
          <div className="text-[11px] font-manrope text-zinc-400">
            <span className={status.active_subscriptions > 0 ? "text-[#00CC66] font-bold" : "text-[#FF3333] font-bold"}>
              {status.active_subscriptions}
            </span>{" "}
            active device{status.active_subscriptions === 1 ? "" : "s"} subscribed
          </div>
          <button
            onClick={sendTest}
            disabled={testing || status.active_subscriptions === 0}
            className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#FF3333]/50 text-[#FF3333] hover:bg-[#FF3333]/10 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="notifications-test-btn"
            title={status.active_subscriptions === 0 ? "Enable notifications first" : "Fire a test push to this device"}
          >
            {testing ? "Sending…" : "Send test push"}
          </button>
        </div>
      )}
      {testMsg && (
        <div className={"mt-2 text-xs font-manrope " + (testMsg.startsWith("✓") ? "text-[#00CC66]" : "text-[#FF3333]")} data-testid="notifications-test-msg">
          {testMsg}
          {!testMsg.startsWith("✓") && status?.active_subscriptions > 0 && (
            <button
              onClick={resetAllSubs}
              className="ml-2 underline text-[#FF3333] hover:text-white font-bold"
              data-testid="notifications-reset-subs-btn"
            >
              Clear all subscriptions
            </button>
          )}
        </div>
      )}

      {denied && (
        <div className="mt-4 text-xs font-manrope text-zinc-300 border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-1" data-testid="notifications-fix-ios">
          <div className="font-bold text-yellow-500 uppercase tracking-[0.15em] text-[10px]">iPhone fix</div>
          <div>1. Open <span className="text-white">iPhone Settings → Notifications</span></div>
          <div>2. Scroll to <span className="text-white">Lazy Lean Method</span> in the app list</div>
          <div>3. Toggle <span className="text-white">Allow Notifications</span> ON</div>
          <div>4. Come back here and refresh this page</div>
        </div>
      )}
    </div>
  );
}
